<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useTablesStore } from 'dashboard/stores/tables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const tablesStore = useTablesStore();

const tables = computed(() => tablesStore.list);
const folders = computed(() =>
  [...tablesStore.folders].sort(
    (a, b) => a.position - b.position || a.name.localeCompare(b.name)
  )
);

// One section per folder (in folder order), then the ungrouped tables. Tables
// keep the server's alphabetical order inside each section.
const sections = computed(() => {
  const byFolder = folder =>
    tables.value.filter(table => (table.folder_id ?? null) === folder);
  return [
    ...folders.value.map(folder => ({
      key: `folder-${folder.id}`,
      folder,
      tables: byFolder(folder.id),
    })),
    { key: 'ungrouped', folder: null, tables: byFolder(null) },
  ];
});

const COLLAPSED_KEY = 'dotchat.tables.collapsedFolders';
const readCollapsed = () => {
  try {
    return JSON.parse(window.localStorage.getItem(COLLAPSED_KEY)) || [];
  } catch {
    return [];
  }
};
const collapsed = ref(readCollapsed());
const toggleSection = key => {
  collapsed.value = collapsed.value.includes(key)
    ? collapsed.value.filter(k => k !== key)
    : [...collapsed.value, key];
  try {
    window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(collapsed.value));
  } catch {
    // Per-browser convenience only.
  }
};

const isCreatingFolder = ref(false);
const newFolderName = ref('');
const renamingFolderId = ref(null);
const folderRenameValue = ref('');
const folderError = ref('');

const submitNewFolder = async () => {
  if (!newFolderName.value.trim()) return;
  folderError.value = '';
  try {
    await tablesStore.createFolder(newFolderName.value.trim());
    newFolderName.value = '';
    isCreatingFolder.value = false;
  } catch {
    folderError.value = t('TABLES.FOLDERS.FOLDER_ERROR');
  }
};

const startFolderRename = folder => {
  renamingFolderId.value = folder.id;
  folderRenameValue.value = folder.name;
};

const submitFolderRename = async () => {
  if (!folderRenameValue.value.trim()) return;
  folderError.value = '';
  try {
    await tablesStore.renameFolder(
      renamingFolderId.value,
      folderRenameValue.value.trim()
    );
    renamingFolderId.value = null;
  } catch {
    folderError.value = t('TABLES.FOLDERS.FOLDER_ERROR');
  }
};

const removeFolder = async folder => {
  const message = t('TABLES.FOLDERS.CONFIRM_DELETE_FOLDER', {
    name: folder.name,
  });
  // eslint-disable-next-line no-alert
  if (!window.confirm(message)) return;
  await tablesStore.deleteFolder(folder.id);
};

const moveTable = async (table, value) => {
  const folderId = value === '' ? null : Number(value);
  if (folderId === (table.folder_id ?? null)) return;
  await tablesStore.moveTable(table.id, folderId);
};

const isCreating = ref(false);
const newTableName = ref('');
const renamingId = ref(null);
const renameValue = ref('');
const errorMessage = ref('');

const openTable = id => {
  router.push({
    name: 'tables_detail',
    params: { accountId: route.params.accountId, tableId: id },
  });
};

const submitNewTable = async () => {
  if (!newTableName.value.trim()) return;
  errorMessage.value = '';
  try {
    const table = await tablesStore.createTable({
      name: newTableName.value.trim(),
    });
    newTableName.value = '';
    isCreating.value = false;
    openTable(table.id);
  } catch (error) {
    errorMessage.value = error?.response?.data?.message || t('TABLES.ERROR');
  }
};

const startRename = table => {
  renamingId.value = table.id;
  renameValue.value = table.name;
};

const submitRename = async () => {
  if (!renameValue.value.trim()) return;
  await tablesStore.updateTable(renamingId.value, {
    name: renameValue.value.trim(),
  });
  renamingId.value = null;
};

const removeTable = async table => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TABLES.CONFIRM_DELETE', { name: table.name }))) return;
  await tablesStore.deleteTable(table.id);
};

onMounted(() => Promise.all([tablesStore.fetch(), tablesStore.fetchFolders()]));
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <div
      class="flex items-center justify-between gap-4 px-6 py-4 border-b border-n-weak flex-shrink-0"
    >
      <h1 class="m-0 text-lg font-semibold text-n-slate-12">
        {{ t('TABLES.TITLE') }}
      </h1>
      <div class="flex items-center gap-2">
        <Button
          :label="t('TABLES.FOLDERS.NEW_FOLDER')"
          icon="i-lucide-folder-plus"
          size="sm"
          variant="faded"
          @click="isCreatingFolder = !isCreatingFolder"
        />
        <Button
          :label="t('TABLES.NEW_TABLE')"
          icon="i-lucide-plus"
          size="sm"
          @click="isCreating = !isCreating"
        />
      </div>
    </div>

    <div
      v-if="isCreatingFolder"
      class="flex items-center gap-2 px-6 py-3 border-b border-n-weak"
    >
      <Input
        v-model="newFolderName"
        :placeholder="t('TABLES.FOLDERS.FOLDER_NAME_PLACEHOLDER')"
        class="w-72"
        @keyup.enter="submitNewFolder"
      />
      <Button :label="t('TABLES.CREATE')" size="sm" @click="submitNewFolder" />
    </div>
    <p v-if="folderError" class="px-6 pt-3 m-0 text-sm text-n-ruby-11">
      {{ folderError }}
    </p>

    <div
      v-if="isCreating"
      class="flex items-center gap-2 px-6 py-3 border-b border-n-weak"
    >
      <Input
        v-model="newTableName"
        :placeholder="t('TABLES.NAME_PLACEHOLDER')"
        class="w-72"
        @keyup.enter="submitNewTable"
      />
      <Button :label="t('TABLES.CREATE')" size="sm" @click="submitNewTable" />
      <span v-if="errorMessage" class="text-sm text-n-ruby-11">{{
        errorMessage
      }}</span>
    </div>

    <div class="flex-1 overflow-y-auto p-6">
      <p
        v-if="!tables.length && !folders.length"
        class="text-sm text-n-slate-11"
      >
        {{ t('TABLES.EMPTY') }}
      </p>

      <template v-else>
        <section
          v-for="section in sections"
          v-show="section.folder || section.tables.length"
          :key="section.key"
          class="mb-6"
        >
          <div class="flex items-center gap-2 mb-3">
            <button
              class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
              @click="toggleSection(section.key)"
            >
              <span
                class="i-lucide-chevron-down size-4 transition-transform"
                :class="{ '-rotate-90': collapsed.includes(section.key) }"
              />
              <span
                :class="section.folder ? 'i-lucide-folder' : 'i-lucide-layers'"
                class="size-4 text-n-slate-11"
              />
              <template
                v-if="!section.folder || renamingFolderId !== section.folder.id"
              >
                {{
                  section.folder
                    ? section.folder.name
                    : t('TABLES.FOLDERS.UNGROUPED')
                }}
              </template>
            </button>
            <template
              v-if="section.folder && renamingFolderId === section.folder.id"
            >
              <Input
                v-model="folderRenameValue"
                class="w-56"
                @keyup.enter="submitFolderRename"
              />
              <Button
                :label="t('TABLES.SAVE')"
                size="xs"
                @click="submitFolderRename"
              />
            </template>
            <span class="text-xs text-n-slate-11">
              {{
                t('TABLES.FOLDERS.FOLDER_COUNT', {
                  count: section.tables.length,
                })
              }}
            </span>
            <template
              v-if="section.folder && renamingFolderId !== section.folder.id"
            >
              <Button
                icon="i-lucide-pencil"
                size="xs"
                variant="ghost"
                color="slate"
                :title="t('TABLES.FOLDERS.RENAME_FOLDER')"
                @click="startFolderRename(section.folder)"
              />
              <Button
                icon="i-lucide-trash-2"
                size="xs"
                variant="ghost"
                color="ruby"
                :title="t('TABLES.FOLDERS.DELETE_FOLDER')"
                @click="removeFolder(section.folder)"
              />
            </template>
          </div>
          <ul
            v-show="!collapsed.includes(section.key)"
            class="grid gap-3 m-0 list-none md:grid-cols-2 xl:grid-cols-3"
          >
            <li
              v-for="table in section.tables"
              :key="table.id"
              class="flex flex-col gap-2 p-4 border rounded-lg border-n-weak bg-n-solid-1"
            >
              <div
                v-if="renamingId === table.id"
                class="flex items-center gap-2"
              >
                <Input
                  v-model="renameValue"
                  class="flex-1"
                  @keyup.enter="submitRename"
                />
                <Button
                  :label="t('TABLES.SAVE')"
                  size="xs"
                  @click="submitRename"
                />
              </div>
              <template v-else>
                <button
                  class="text-base font-medium text-left text-n-slate-12 hover:underline"
                  @click="openTable(table.id)"
                >
                  {{ table.name }}
                </button>
                <!-- The slug is the machine-facing identity a bot names this
                 table by, so it is shown alongside the display name. -->
                <code class="text-xs text-n-slate-11">{{ table.slug }}</code>
                <p v-if="table.description" class="m-0 text-sm text-n-slate-11">
                  {{ table.description }}
                </p>
                <div class="flex gap-2 mt-2">
                  <Button
                    :label="t('TABLES.OPEN')"
                    size="xs"
                    variant="faded"
                    @click="openTable(table.id)"
                  />
                  <Button
                    :label="t('TABLES.RENAME')"
                    size="xs"
                    variant="faded"
                    @click="startRename(table)"
                  />
                  <Button
                    :label="t('TABLES.DELETE')"
                    size="xs"
                    color="ruby"
                    variant="faded"
                    @click="removeTable(table)"
                  />
                </div>
                <label
                  v-if="folders.length"
                  class="flex items-center gap-2 text-xs text-n-slate-11"
                >
                  {{ t('TABLES.FOLDERS.MOVE_TO_FOLDER') }}
                  <select
                    :value="table.folder_id ?? ''"
                    class="!mb-0 !h-7 !py-0 text-xs rounded-md w-auto"
                    @change="moveTable(table, $event.target.value)"
                  >
                    <option value="">
                      {{ t('TABLES.FOLDERS.UNGROUPED') }}
                    </option>
                    <option
                      v-for="folder in folders"
                      :key="folder.id"
                      :value="folder.id"
                    >
                      {{ folder.name }}
                    </option>
                  </select>
                </label>
              </template>
            </li>
          </ul>
        </section>
      </template>
    </div>
  </div>
</template>
